import type { ProjectSummary } from "@/lib/api";

export type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

export interface EffectiveStatus {
  label: string;
  variant: BadgeVariant;
}

const STATUS_LABELS: Record<string, EffectiveStatus> = {
  pending: { label: "Pending", variant: "secondary" },
  creating_repo: { label: "Creating repo", variant: "outline" },
  scaffolding: { label: "Scaffolding", variant: "outline" },
  preparing: { label: "Preparing", variant: "outline" },
  pushing: { label: "Pushing", variant: "outline" },
  generating_instructions: { label: "Generating instructions", variant: "outline" },
  completed: { label: "Completed", variant: "default" },
  failed: { label: "Failed", variant: "destructive" },
  cancelled: { label: "Cancelled", variant: "secondary" },
};

/** Derive the user-facing status label and badge variant for a project. */
export function getEffectiveStatus(project: ProjectSummary): EffectiveStatus {
  return (
    STATUS_LABELS[project.status] ?? {
      label: project.status,
      variant: "secondary" as const,
    }
  );
}

// A project flips to "completed" when the scaffold finishes, but its deployed
// app_url only lands minutes later via the deploy callback (NOR-292). This is
// the window during which the UI shows a "Deploying..." state and polls for
// the URL -- bounded so a project whose deploy never reports back (an old one,
// or a failed deploy) doesn't sit in "Deploying..." or poll forever.
export const DEPLOY_URL_WINDOW_MS = 30 * 60 * 1000; // ~30 min, covers a slow deploy

/** Either shape the deploy callback records an outcome on: the project itself
 * (dev) or a promoted environment row (test/prod) -- see `ProjectSummary` and
 * `ProjectEnvironment` (NOR-456). */
interface DeployOutcome {
  deploy_status?: string;
  deploy_run_url?: string | null;
}

/** Whether the last deploy reported for this project (dev) or environment
 * (test/prod) failed. `deploy_status` defaults to `"success"` on the backend,
 * so a row from before this column existed, or an older scaffolded repo that
 * only ever calls back on success, reads as healthy here too. */
export function hasFailedDeploy(target: DeployOutcome): boolean {
  return target.deploy_status === "failed";
}

/** Whether a project has finished scaffolding but is still waiting on its
 * deployed app URL from the deploy callback (within the deploy window). A
 * deploy already reported as failed is never "awaiting" -- there is nothing
 * left to poll for until the next deploy run reports back. Nor is a project
 * whose Azure resources were torn down (`azure_removed_at`): it has no app
 * URL because it has no app, not because one is on its way. */
export function isAwaitingDeployUrl(project: ProjectSummary): boolean {
  return (
    project.status === "completed" &&
    !project.app_url &&
    !project.azure_removed_at &&
    !hasFailedDeploy(project) &&
    Date.now() - new Date(project.created_at).getTime() < DEPLOY_URL_WINDOW_MS
  );
}
